/**
 * TanStack Query hooks for `automation-crud-permission-scope`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	remove,
	update,
	automationCrudPermissionScopeQueryKeys,
	type CreateRoleScopeBody,
	type UpdateRoleScopeBody,
	type ListRoleScopeResponse,
	type RoleScopeRow,
} from "./api";

export function useAutomationCrudPermissionScopeList(): UseQueryResult<ListRoleScopeResponse, Error> {
	return useQuery({
		queryKey: automationCrudPermissionScopeQueryKeys.list(),
		queryFn: list,
	});
}

export function useAutomationCrudPermissionScopeCreate(): UseMutationResult<RoleScopeRow, Error, CreateRoleScopeBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: automationCrudPermissionScopeQueryKeys.all });
		},
	});
}

export function useAutomationCrudPermissionScopeUpdate(): UseMutationResult<
	RoleScopeRow,
	Error,
	{ id: string; body: UpdateRoleScopeBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }) => update(id, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: automationCrudPermissionScopeQueryKeys.all });
		},
	});
}

export function useAutomationCrudPermissionScopeDelete(): UseMutationResult<{ removed: boolean }, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: automationCrudPermissionScopeQueryKeys.all });
		},
	});
}
